import { useEffect, useRef, type ReactNode, type UIEvent } from "react";
import { Sparkles } from "lucide-react";
import type { UIMessage } from "@/hooks/use-agent-chat";
import { MessageBlockRenderer } from "./agent-blocks";
import { cn } from "@/lib/utils";

interface Props {
  messages: UIMessage[];
  isStreaming: boolean;
  onSuggestionClick?: (text: string) => void;
  disabled?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  suggestions?: string[];
  supplemental?: ReactNode;
}

const SUGGESTIONS = [
  "What POs are waiting on a reply?",
  "Show me projects assigned to me",
  "Book a one-off shipment from Dallas to Chicago",
  "Find order by number",
];

const PINNED_BOTTOM_THRESHOLD_PX = 80;

function assistantAnnouncement(
  messages: UIMessage[],
  isStreaming: boolean,
): string {
  if (isStreaming) return "Assistant is responding.";

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role !== "assistant") continue;
    if (message.status === "streaming") return "Assistant is responding.";
    if (message.status === "complete") return "Assistant response completed.";
    if (message.status === "errored")
      return "Assistant response encountered an error.";
    return "Assistant response cancelled.";
  }

  return "";
}

export function AgentMessages({
  messages,
  isStreaming,
  onSuggestionClick,
  disabled,
  emptyTitle = "How can I help?",
  emptyDescription = "I can look things up, update projects and orders, and book shipments.",
  suggestions = SUGGESTIONS,
  supplemental,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);
  const scrollFrame = useRef<number | null>(null);
  const announcement = assistantAnnouncement(messages, isStreaming);

  useEffect(() => {
    if (!pinnedToBottom.current || scrollFrame.current !== null) return;
    scrollFrame.current = window.requestAnimationFrame(() => {
      scrollFrame.current = null;
      const list = listRef.current;
      if (!list || !pinnedToBottom.current) return;
      if (typeof list.scrollTo === "function") {
        list.scrollTo({ top: list.scrollHeight, behavior: "auto" });
      } else {
        list.scrollTop = list.scrollHeight;
      }
    });
  }, [messages, isStreaming, supplemental]);

  useEffect(
    () => () => {
      if (scrollFrame.current !== null) {
        window.cancelAnimationFrame(scrollFrame.current);
        scrollFrame.current = null;
      }
    },
    [],
  );

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    pinnedToBottom.current =
      list.scrollHeight - list.scrollTop - list.clientHeight <=
      PINNED_BOTTOM_THRESHOLD_PX;
  };

  if (messages.length === 0 && !supplemental) {
    return (
      <>
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {announcement}
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="text-sm font-medium">{emptyTitle}</div>
          <div className="text-xs text-muted-foreground">
            {emptyDescription}
          </div>
          <div className="mt-2 grid w-full gap-1.5">
            {suggestions.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => onSuggestionClick?.(s)}
                disabled={disabled || !onSuggestionClick}
                className="min-h-11 rounded-md border px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-60 sm:min-h-9 [@media(pointer:coarse)]:min-h-11"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
      <div
        ref={listRef}
        role="log"
        aria-label="Conversation messages"
        aria-live="polite"
        aria-relevant="additions"
        onScroll={handleScroll}
        className="flex-1 space-y-3 overflow-y-auto overscroll-y-contain px-3 py-3"
      >
        {messages.map((m) => (
          <div
            key={m.uuid}
            className={cn(
              "rounded-lg px-3 py-2 text-sm",
              m.role === "user" ? "bg-primary/10 ml-6" : "bg-card mr-6 border",
              m.status === "errored" &&
                "border-destructive/50 bg-destructive/5",
            )}
          >
            {m.blocks.length === 0 &&
            m.role === "assistant" &&
            m.status === "streaming" ? (
              <div className="text-xs text-muted-foreground italic">
                Thinking…
              </div>
            ) : (
              m.blocks.map((b, i) => <MessageBlockRenderer key={i} block={b} />)
            )}
          </div>
        ))}
        {supplemental}
        {messages.length === 0 ? (
          <div className="flex min-h-48 flex-col items-center justify-center gap-3 px-3 py-6 text-center">
            <div className="text-sm font-medium">{emptyTitle}</div>
            <div className="text-xs text-muted-foreground">
              {emptyDescription}
            </div>
            <div className="grid w-full gap-1.5">
              {suggestions.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  onClick={() => onSuggestionClick?.(suggestion)}
                  disabled={disabled || !onSuggestionClick}
                  className="min-h-11 rounded-md border px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-60 sm:min-h-9 [@media(pointer:coarse)]:min-h-11"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
